import { Box, MenuItem, TextField } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PaloStatusChangeDialog } from '../../../../common/components/palo/palo-status-change-dialog';
import { Prefix } from '../../../../common/models/prefix';
import { invoiceNumberSequencesSupplierHistorySelector } from '../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useCompletionWarnings } from '../../../../common/components/palo/use-completion-warnings';

const c = tokens.color;

export interface PaloCompleteInvoiceNumberSequenceDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (prefix?: Prefix) => void;
    taxes?: number[];
    invoiceLocation?: string;
    invoiceId?: number;
}

/**
 * Completing an invoice under a different number range (#2132, wave 4).
 *
 * Same step as PaloCompleteDocumentDialog, with one thing more to decide: the
 * prefix the invoice number is drawn from. It is the only choice here that
 * cannot be taken back afterwards, so the confirmation stays closed until one
 * is picked.
 */
export const PaloCompleteInvoiceNumberSequenceDialog = ({
    open,
    onClose,
    onConfirm,
    taxes,
    invoiceLocation,
    invoiceId,
}: PaloCompleteInvoiceNumberSequenceDialogProps): ReactElement => {
    const intl = useIntl();
    const prefixes = useAppSelector(invoiceNumberSequencesSupplierHistorySelector);
    const warnings = useCompletionWarnings({ taxes, invoiceLocation });
    const [prefixId, setPrefixId] = useState('');

    useEffect(() => {
        if (open) {
            setPrefixId('');
        }
    }, [open]);

    const chosen = prefixes?.find(prefix => String(prefix.id) === prefixId);

    return (
        <PaloStatusChangeDialog
            open={open}
            onClose={onClose}
            title={<FormattedMessage id="app.invoices.detail.finishChangeNumberSequence" />}
            description={<PoolFormattedMessage id="app.invoices.detail.finishChangeNumberSequence.text" />}
            confirmLabel={<FormattedMessage id="app.common.complete" />}
            acknowledgements={warnings.map(id => (
                <PoolFormattedMessage key={id} id={id} />
            ))}
            confirmDisabled={!chosen}
            documentType={DocumentType.Invoice}
            documentId={invoiceId}
            onConfirm={() => onConfirm(chosen)}
        >
            <Box sx={{ fontSize: '11px', color: c.ink2, paddingBottom: '4px' }}>
                <FormattedMessage id="app.invoices.detail.finishChangeNumberSequence.prefix.label" />
            </Box>
            <TextField
                select={true}
                value={prefixId}
                onChange={event => setPrefixId(event.target.value)}
                size="small"
                fullWidth={true}
            >
                {(prefixes ?? []).map(prefix => (
                    <MenuItem key={prefix.id} value={String(prefix.id)}>
                        {prefix.isDefault
                            ? `${prefix.prefix ?? '-'} (${intl.formatMessage({ id: 'app.common.standard' })})`
                            : prefix.prefix ?? '-'}
                    </MenuItem>
                ))}
            </TextField>
        </PaloStatusChangeDialog>
    );
};
